<script setup>
import ErrorNotice from '../components/feedback/ErrorNotice.vue'
import { onBeforeUnmount, ref, watch } from 'vue'
import { prizeApi } from '../api'
import { getToken, memberSession } from '../api/session'
import { displayTime, emptyReceipt, receiptPayload, prizeBonusProgress } from '../prize/receipt'
import PrizeReceiptFields from '../components/PrizeReceiptFields.vue'
import PageState from '../components/feedback/PageState.vue'

const props = defineProps({ orderId: { type: String, default: '' } })
const emit = defineEmits(['navigate', 'login'])
const orders = ref([])
const order = ref(null)
const total = ref(0)
const page = ref(1)
const receipt = ref(emptyReceipt())
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const saved = ref('')
let sequence = 0
async function load(targetPage = page.value) {
  const current = ++sequence
  const token = getToken()
  loading.value = Boolean(token); error.value = ''; order.value = null; orders.value = []; receipt.value = emptyReceipt(); busy.value = false
  if (!token) { total.value = 0; return }
  try {
    if (props.orderId) {
      const result = await prizeApi.detail(props.orderId)
      if (current !== sequence || token !== getToken()) return
      if (!result?.id) throw new Error('订单不存在')
      order.value = result
      receipt.value = Object.fromEntries(Object.keys(emptyReceipt()).map((key) => [key, result[key] || '']))
    } else {
      const result = await prizeApi.page(targetPage)
      if (current !== sequence || token !== getToken()) return
      if (!Array.isArray(result?.data)) throw new Error('订单加载失败，请重试')
      orders.value = result.data; total.value = result.total; page.value = targetPage
    }
  } catch (cause) { if (current === sequence) error.value = cause.message }
  finally { if (current === sequence) loading.value = false }
}
async function saveReceipt() {
  if (busy.value || !order.value?.canModifyReceipt || !getToken()) return
  error.value = ''; saved.value = ''
  let payload
  try { payload = receiptPayload(receipt.value) } catch (cause) { error.value = cause.message; return }
  const current = sequence
  const token = getToken()
  busy.value = true
  try {
    await prizeApi.receipt(props.orderId, payload)
    if (current !== sequence || token !== getToken()) return
    await load()
    if (token === getToken() && !error.value) saved.value = '收货资料已保存'
  } catch (cause) { if (current === sequence) error.value = cause.message }
  finally { if (current === sequence) busy.value = false }
}
watch([() => props.orderId, memberSession], () => { saved.value = ''; page.value = 1; load(1) }, { immediate: true })
onBeforeUnmount(() => { ++sequence })
</script>
<template>
  <div class="container member-page prize-orders-page">
    <div class="member-actions"><button class="outline-button" @click="emit('navigate', orderId ? '/mine/prizes' : '/activity/discount')">{{ orderId ? '← 我的奖品' : '← 优惠活动' }}</button><button class="outline-button" :disabled="loading || busy" @click="saved = ''; load()">刷新状态</button></div>
    <header class="prize-page-heading"><h1>{{ orderId ? '奖品订单详情' : '我的奖品' }}</h1><p>查看奖品申请进度、发放结果与收货资料</p></header>
    <PageState v-if="!memberSession" title="登录后查看本人奖品订单" description="奖品申请、物流与发放记录仅对本人展示"><button type="button" class="primary" @click="emit('login')">会员登录</button></PageState>
    <PageState v-else-if="loading" loading title="订单加载中…" />
    <template v-else>
      <PageState v-if="error && !order" :error="error" @retry="load()" />
      <ErrorNotice v-else-if="error" class="request-error" :message="error"></ErrorNotice>
      <p v-if="saved" role="status" class="receipt-saved">{{ saved }}</p>
      <article v-if="order" class="member-panel">
        <div class="member-actions"><h2>{{ order.prizeName }}</h2><strong class="prize-status">{{ order.statusName }}</strong></div>
        <dl class="order-details"><dt>订单号</dt><dd>{{ order.orderNo }}</dd><dt>所属活动</dt><dd>{{ order.activityTitle || '—' }}</dd><dt>奖品数量</dt><dd>{{ order.quantity }}</dd><template v-if="order.prizeType === 'BONUS'"><dt>红利金额</dt><dd>{{ order.bonusAmount }} 元</dd><dt>入账时间</dt><dd>{{ displayTime(order.paidAt) }}</dd></template><dt>申请时间</dt><dd>{{ displayTime(order.appliedAt) }}</dd><template v-if="order.shippedAt"><dt>发放时间</dt><dd>{{ displayTime(order.shippedAt) }}</dd></template><template v-if="order.trackingNo"><dt>物流公司</dt><dd>{{ order.trackingCompany }}</dd><dt>物流单号</dt><dd>{{ order.trackingNo }}</dd></template><template v-if="order.fulfillmentInfo"><dt>兑换说明</dt><dd class="fulfillment-info">{{ order.fulfillmentInfo }}</dd></template></dl>
        <form v-if="order.canModifyReceipt" class="prize-form" @submit.prevent="saveReceipt"><PrizeReceiptFields v-model="receipt" :disabled="busy" /><p>待审核时可以修改，审核后收货资料将锁定。</p><button class="primary" :disabled="busy">{{ busy ? '保存中…' : '保存收货资料' }}</button></form>
        <template v-else-if="order.prizeType === 'PHYSICAL'"><h3>收货资料</h3><p>{{ order.recipientName }} · {{ order.recipientPhone }}</p><p>{{ order.shippingAddress }}</p><p>当前状态不允许修改收货资料。</p></template>
        <p v-if="prizeBonusProgress(order)" role="status">{{ prizeBonusProgress(order) }}</p>
      </article>
      <template v-else-if="!orderId && !error">
        <PageState v-if="!orders.length" title="暂无奖品申请" description="前往优惠活动查看可申请的奖品"><button type="button" class="primary" @click="emit('navigate', '/activity/discount')">查看优惠活动</button></PageState>
        <div v-else class="order-table"><table><thead><tr><th>订单号</th><th>所属活动 / 奖品</th><th>数量</th><th>状态</th><th>申请时间</th><th>操作</th></tr></thead><tbody><tr v-for="item in orders" :key="item.id"><td>{{ item.orderNo }}</td><td>{{ item.activityTitle }}<br /><strong>{{ item.prizeName }}</strong></td><td>{{ item.quantity }}</td><td>{{ item.statusName }}</td><td>{{ displayTime(item.appliedAt) }}</td><td><button class="outline-button" @click="emit('navigate', `/mine/prizes/${item.id}`)">查看详情</button></td></tr></tbody></table></div>
        <nav v-if="total > 0" class="member-actions pagination" aria-label="奖品分页"><span>共 {{ total }} 条 · 第 {{ page }} 页</span><button class="outline-button" :disabled="page <= 1" @click="load(page - 1)">上一页</button><button class="outline-button" :disabled="page * 10 >= total" @click="load(page + 1)">下一页</button></nav>
      </template>
    </template>
  </div>
</template>
<style scoped src="../prize/prize.css"></style>
<style scoped>
.prize-orders-page { min-height: 580px; padding: 24px; border-radius: 12px; background: #fff; }
.prize-page-heading { margin: 24px 0; }
.prize-page-heading h1 { margin: 0; color: #303442; font-size: 24px; font-weight: 600; line-height: 1.5; }
.prize-page-heading p { margin: 8px 0 0; color: #6e7f98; font-size: 14px; line-height: 1.7; }
.member-actions:first-child { justify-content: space-between; }
.member-panel { min-height: 0; margin-bottom: 0; padding: 24px 0 0; border: 0; border-top: 1px solid #e7edf5; border-radius: 0; }
.member-panel h2 { margin: 0; font-size: 20px; line-height: 1.5; overflow-wrap: anywhere; }
.member-panel .member-actions { justify-content: space-between; }
.outline-button { min-height: 42px; font-size: 14px; transition: background .18s, border-color .18s; }
.outline-button:hover:not(:disabled) { border-color: #9dc5e5; background: #f0f7ff; }
button:focus-visible { outline: 3px solid #80b8e5; outline-offset: 3px; }
.order-details { padding: 24px; border-radius: 10px; background: #f8fafd; font-size: 14px; }
.order-table { font-size: 14px; }
.order-table tr:last-child td { border-bottom: 0; }
.pagination { color: #6e7f98; font-size: 13px; }
.prize-status { border-radius: 6px; font-size: 13px; font-weight: 500; }
.prize-form > p { font-size: 13px; }
.receipt-saved { padding: 12px 16px; border-radius: 8px; color: #26745e; background: #edf8f3; font-size: 14px; }
.request-error { padding: 12px 16px; border-radius: 8px; background: #fff3f2; }
@media (max-width: 600px) { .prize-orders-page { padding: 20px 16px; min-height: 480px; } .order-details { grid-template-columns: 80px minmax(0, 1fr); gap: 12px; padding: 16px; } .member-actions { gap: 8px; } .outline-button { padding: 9px 12px; } .pagination { justify-content: center; } }
@media (prefers-reduced-motion: reduce) { .outline-button { transition: none; } }
</style>
